import Vue, { type CreateElement } from 'vue';

import VueMeta from 'vue-meta';

import VueFinalModal from 'vue-final-modal';

import 'lazysizes';

import type { Context } from '@/@types/ssr';

import App from '@/App.vue';

import { getApolloProvider } from '@/graphql/apollo';
import { createI18n } from '@/i18n';
import { createRouter } from '@/routing';
import createStore from '@/store';

import {
	initApp,
	initClientStorage,
	initFirebase,
	initLanguage,
	initLocale,
	initRouter,
	initSSR,
	initStore,
	jwIdServerClientHandover,
	refreshToken,
	initJwId,
	initUncriticalRequests,
} from '@/main.common';

import { createThumborFilter } from '@/filters/thumbor';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { setVm } from '@/helpers/vm-helper';

import { GlobalComponentsPlugin } from '@/plugins/global-components';
import { GlobalDirectivesPlugin } from '@/plugins/global-directives';
import Jw, { Options as JwOptions } from '@/plugins/jw';
import Ready, { Options as ReadyOptions } from '@/plugins/ready';
import type { ApolloProvider } from 'vue-apollo';
import { storeUserGeoLocation } from './helpers/geo-location-helper';
import { storeSrDebuggerInfo } from '@/features/sponsored-recommendations/helpers/sponsoredrec-debugging-tools';
import { storeNoIndexDebug } from './helpers/query-parameters-helper';
import { Domain, detectDomain } from '@/routing/helpers/subdomain-helper';

Vue.config.ignoredElements = [/^ion-/];
Vue.config.productionTip = process.env.NODE_ENV !== 'production';
Vue.config.performance = process.env.NODE_ENV === 'production';

Vue.config.errorHandler = (error, _, info) => {
	// mObs - next steps: sentry may not be initialized yet here, consider a queue system to avoid lost errors
	import('@/helpers/sentry-helper').then(({ captureMessageForSentry }) => {
		captureMessageForSentry(
			'[Uncaught Error]:',
			{ where: '[main.ssr.ts]: Vue Error Handler', error, meta: { info } },
			'error'
		);
	});
};

Vue.use(VueMeta, {});

global.IS_TOUCH = process.client ? !window.matchMedia('(hover: hover)').matches : false;

// Snapshot window.__DATA__.state.user immediately before anything can modify it
if (process.client && (window as any).__DATA__?.state?.user) {
	// Deep clone just the user state to preserve original SSR state
	(window as any).__INITIAL_SSR_USER__ = JSON.parse(JSON.stringify((window as any).__DATA__?.state?.user ?? {}));
	// check if jw_user exist in document.cookie
	(window as any).__INITIAL_USER_COOKIE_AVAILABILITY__ = !!document.cookie
		.split('; ')
		.some(cookie => cookie.startsWith('jw_user='));
	(window as any).__INITIAL_USER_LOCALSTORAGE_AVAILABILITY__ = !!localStorage.getItem('jw/user');
}

Vue.use(GlobalComponentsPlugin);
Vue.use(GlobalDirectivesPlugin);
Vue.use(VueFinalModal);

// constantly check `window.hydrationBailed` for hydration issues
async function checkHydrationBails() {
	if ('hydrationBailed' in window && Array.isArray(window.hydrationBailed) && window.hydrationBailed.length > 0) {
		const bails = [...window.hydrationBailed] as { cause: string; where: string; detail: { parent: any } }[];
		window.hydrationBailed = [];
		// fire hydration bails to sentry
		const { captureMessageForSentry } = await import('@/helpers/sentry-helper');
		for (const bail of bails) {
			const { cause, where, detail } = bail;
			const title = where
				.split(' ')
				// only take last 3 portions of css selectors (-5 because " > " are also present)
				.slice(-5)
				// remove :nth-child() from selector to better group issues
				.map(item => item.split(':')[0])
				.join(' ');
			const error = new Error(cause);
			// Vnodes have additional information attached to the Vue component
			const parentComponentName = ((detail as any).VNodes?.[0]?.context?.$vnode.tag || 'none')
				.split('-')
				.slice(-1)
				.join('');
			error.name = `[HydrationBail] ${parentComponentName} ${title}`;
			console.error('[HydrationBail]', { cause, where, detail, parent: parentComponentName });
			TrackingHelper.trackEvent('performance_metrics', {
				action: 'measure',
				property: 'hydration',
				label: `${parentComponentName}###${where}`,
				nonInteraction: true,
			});
			captureMessageForSentry(`[HydrationBail]`, { error, where, parent: parentComponentName }, 'error');
		}
	}
}
if (process.client) {
	setInterval(checkHydrationBails, 1000);
}

// START: CLS Check
if (process.env.NODE_ENV !== 'production') {
	import('@/plugins/cls-check').then(module => Vue.use(module.ClsCheckPlugin));
}
// END: CLS Check

export default async (context: Context) => {
	const didGenerateId = initJwId(context);

	// pass context down to App instance and all Components
	Vue.prototype.$context = context;

	await initFirebase();

	/***** SERVER ENV *****/
	if (process.server) {
		const customHeaders = context.ssr.data?.SERVER_ENV?.customHeaders;

		if (customHeaders) {
			const { addCustomHeadersToAxios } = await import('@/helpers/axios-server-helper');
			addCustomHeadersToAxios(customHeaders);
		}
	}
	/***** SERVER ENV *****/

	let localStoragePromise;
	let ionicPromise;
	if (process.client) {
		// Start loading Ionic
		ionicPromise = import('@/ionic.loader');
		localStoragePromise = initClientStorage();
	}

	if (BUILD_CONFIG.globals.BUILD_TARGET === 'SSR') {
		// Make basic SSR init before init Store
		initSSR(context, didGenerateId);
	}

	await localStoragePromise;
	const store = await createStore(context);
	context.store = store;

	let refreshTokenPromise;
	if (process.client) {
		refreshTokenPromise = refreshToken(context);
	}

	/***** JW PLUGIN *****/
	Vue.use<JwOptions>(Jw);
	/***** READY STORE *****/
	Vue.use<ReadyOptions>(Ready, { store });

	const i18n = createI18n();
	context.i18n = i18n;

	if (process.client) {
		await storeUserGeoLocation();
		storeSrDebuggerInfo();

		// temp solution to set debug params in session storage in meta.store
		storeNoIndexDebug();
	}

	context.webLocale = await initLocale(context);

	// Detect current domain for both server and client
	let currentDomain: Domain = Domain.MAIN;

	if (process.server && context.req) {
		const rawHeaders = context.req.rawHeaders;
		const headers = context.req.headers;

		// Extract site header value from rawHeaders array
		let siteHeaderValue = '';
		for (let i = 0; i < rawHeaders.length; i += 2) {
			if (rawHeaders[i].toLowerCase() === 'site') {
				siteHeaderValue = rawHeaders[i + 1];
				break;
			}
		}

		// Extract Host header value from rawHeaders array
		let hostHeaderValue = '';
		for (let i = 0; i < rawHeaders.length; i += 2) {
			if (rawHeaders[i].toLowerCase() === 'host') {
				hostHeaderValue = rawHeaders[i + 1];
				break;
			}
		}

		if (!hostHeaderValue && headers.host) {
			hostHeaderValue = headers.host;
		}

		let hostname =
			siteHeaderValue || hostHeaderValue || headers['x-forwarded-host'] || headers['x-original-host'] || '';

		if (!hostname && context.req.url) {
			if (headers.referer) {
				try {
					const refUrl = new URL(headers.referer);
					hostname = refUrl.hostname;
				} catch (e) {}
			}

			// Fallback: check if we're being crawled by a bot that might include domain info in User-Agent or other headers
			if (!hostname) {
				const userAgent = headers['user-agent'] || '';

				if (userAgent.toLowerCase().includes('bot')) {
					// Check if referer contains any known subdomain pattern
					const referer = headers.referer || '';
					const knownSubdomains = Object.values(Domain).filter(domain => domain !== Domain.MAIN);

					// Look for any known subdomain in the referer
					const matchedSubdomain = knownSubdomains.find(subdomain => referer.includes(`${subdomain}.`));

					if (matchedSubdomain) {
						hostname = `${matchedSubdomain}.${JW_CONFIG.DOMAIN}`;
					} else {
						hostname = `www.${JW_CONFIG.DOMAIN}`;
					}
				}
			}
		}

		currentDomain = detectDomain(hostname);
	} else if (process.client) {
		currentDomain = detectDomain(window.location.hostname);
	}

	// Set the active domain in the store
	store.dispatch('routing/setActiveDomain', currentDomain);

	// Pass the currentDomain directly to createRouter
	const router = createRouter(context.webLocale, store, currentDomain);
	context.router = router;

	// Initialize Sentry early with router to catch all initialization errors
	if (process.client) {
		import('@/helpers/sentry-helper').then(({ initSentry }) =>
			initSentry({
				router,
				jwId: store.state?.user?.jwId,
				xServeTrack: store?.state?.app?.xServeTrack,
				isSeoUser: context?.ssrData?.isSeoUser,
				isLoggedIn: store?.getters?.['user/isLoggedIn'],
				buyboxView: store.state?.user?.newBuyboxView,
				buyboxExpView: store.state?.user?.buyboxExpView,
				buyboxPresentationType: store.state?.user?.preferences?.buyboxPresentationType,
				buyboxTooltipAcknowledged: store.state?.user?.newBuyboxTooltipAcknowledged,
				country: store?.getters?.['language/country'],
				newBuyboxFilterType: store.state?.user?.newBuyboxFilterType,
			})
		);
	}

	// Make current domain available to all Vue components for SmartLink domain detection
	Vue.prototype.$domain = currentDomain;

	// This line MUST come after `await initLocale(context)`
	// It's here because `initLanguage` needs the router to check if we're on a sports route
	context.language = await initLanguage(context);

	Vue.filter('Thumbor', createThumborFilter());

	TrackingHelper.resetState();
	TrackingHelper.setStore(store);

	if (process.client) {
		// jwId handover needs to happen before we await for refreshTokenPromise,
		// because if there is an error with the refresh token, it might await ReadyType.JW_ID endlessly
		await jwIdServerClientHandover(store);
	}

	// wait for the refreshToken promise to finish Apollo init (it needs a fresh access token)
	await refreshTokenPromise;

	let apolloProvider: ApolloProvider<any>;
	if (BUILD_CONFIG.globals.BUILD_TARGET === 'SPA') {
		// If SPA we need to wait for the store to be created to make basic SPA init
		const { initSPA } = await import(/* webpackChunkName: "main-common" */ '@/main.common');
		apolloProvider = await getApolloProvider({ store });

		await initSPA(context, apolloProvider);
	}

	/**
	 * Backend Tracking
	 * Forward HTTP request user-agent to apollo requests
	 * Used for Tracking Mutations
	 **/
	let extraHeaders = {};
	if (process.server) {
		const UserAgent = context?.req?.headers['user-agent'];
		if (UserAgent && UserAgent.indexOf('GoogleStackdriverMonitoring-UptimeChecks') === -1) {
			extraHeaders = {
				'x-forwarded-user-agent': UserAgent,
			};
		}
	}

	let xServeTrack = null;
	if (process.client) {
		xServeTrack = store?.state?.app?.xServeTrack;
	}
	if (process.server) {
		const contextXServeTrack = context?.ssrData?.xServeTrack;
		if (contextXServeTrack) {
			xServeTrack = contextXServeTrack;
		}
	}

	if (xServeTrack) {
		extraHeaders = {
			...extraHeaders,
			'x-environment': xServeTrack,
		};
	}

	/***** Vue Apollo init *****/
	// @ts-ignore
	if (!apolloProvider) {
		apolloProvider = await getApolloProvider({
			headers: {
				...context.ssr?.data?.SERVER_ENV?.customHeaders,
				...extraHeaders,
			},
			store,
		});
	}
	/***** Vue Apollo init *****/

	/***** SERVER ENV *****/
	if (process.server) {
		// clear SERVER_ENV to avoid leaks
		delete context.ssr.data.SERVER_ENV;
	}
	/***** SERVER ENV *****/
	await initStore(context);

	// all requests that are used on pretty much all pages and are un-personalised and cacheable
	const uncriticalPromise = initUncriticalRequests(context);

	await initRouter(context, apolloProvider);

	await initApp(context);

	if (process.client) {
		await ionicPromise;
		const Ionic = (await ionicPromise)?.default;
		Ionic && Vue.use(Ionic);
	}

	context.app = new Vue({
		router,
		store,
		i18n,
		apolloProvider,
		render: (h: CreateElement) => h(App),
	});

	setVm(context.app);

	// making sure that we got all uncritical requests done before we ship HTML back
	await Promise.all(uncriticalPromise);

	return context;
};
